import React, { useState } from 'react';
import { useQuery } from '@apollo/client';
import { Card, Row, Col, Statistic, Spin, DatePicker } from 'antd';
import { UsergroupAddOutlined, PlaySquareOutlined, RiseOutlined } from '@ant-design/icons';
import { Column } from '@ant-design/plots';
import dayjs from 'dayjs';
import { subscriberUsageStats } from '../../graphql/queries';

const { RangePicker } = DatePicker;

const SUBSCRIPTION_TYPES = [
  { key: 'txtToVideo',  label: 'TXT2VID',     color: '#1677ff' },
  { key: 'imgToVideo',  label: 'IMG2VID',     color: '#52c41a' },
  { key: 'videoTools',  label: 'Video Tools', color: '#fa8c16' },
  { key: 'textToImage', label: 'Text2Image',  color: '#722ed1' },
  { key: 'imgToImg',    label: 'Img2Img',     color: '#eb2f96' },
  { key: 'imageTools',  label: 'Image Tools', color: '#13c2c2' },
];

const buildDateRange = (dates) => {
  if (!dates || !dates[0] || !dates[1]) return { startDate: null, endDate: null };
  return {
    startDate: dates[0].startOf('day').toISOString(),
    endDate: dates[1].endOf('day').toISOString(),
  };
};

const SubscriberStats = () => {
  const defaultRange = [dayjs().subtract(30, 'days'), dayjs()];
  const [dateRange, setDateRange] = useState(defaultRange);
  const { startDate, endDate } = buildDateRange(dateRange);

  const { loading, data } = useQuery(subscriberUsageStats, {
    variables: { startDate, endDate },
    skip: !startDate || !endDate,
  });

  const stats = data?.subscriberUsageStats;
  const total = stats?.totalGenerations ?? 0;

  const dailyData = (stats?.dailyBreakdown ?? [])
    .slice()
    .sort((a, b) => (a.date < b.date ? -1 : 1))
    .map((d) => ({ date: d.date, count: d.count }));

  const columnConfig = {
    data: dailyData,
    xField: 'date',
    yField: 'count',
    color: '#1677ff',
    columnWidthRatio: 0.6,
    label: null,
    xAxis: { label: { autoRotate: true } },
    yAxis: { nice: true },
    tooltip: { formatter: (d) => ({ name: 'Generations', value: d.count }) },
  };

  return (
    <div style={{ marginTop: 24 }}>
      <Card
        title="Subscriber Usage"
        extra={
          <RangePicker
            value={dateRange}
            onChange={(dates) => setDateRange(dates)}
            allowClear={false}
          />
        }
        style={{ marginBottom: 24 }}
      >
        <Spin spinning={loading}>
          {/* Metric Cards */}
          <Row gutter={16} style={{ marginBottom: 24 }}>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Subscribers (paying, in range)"
                  value={stats?.subscriberCount ?? 0}
                  prefix={<UsergroupAddOutlined />}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Total Generations"
                  value={stats?.totalGenerations ?? 0}
                  prefix={<PlaySquareOutlined />}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card>
                <Statistic
                  title="Per 1K Paying Users"
                  value={stats?.per1KUsers ?? 0}
                  precision={2}
                  prefix={<RiseOutlined />}
                />
              </Card>
            </Col>
          </Row>

          {/* Charts */}
          <Row gutter={24}>
            <Col span={14}>
              <Card title="Daily Generations (Subscribers)" bordered={false}>
                {dailyData.length === 0 ? (
                  <div style={{ textAlign: 'center', color: '#999', padding: '40px 0' }}>
                    No data for selected range
                  </div>
                ) : (
                  <Column {...columnConfig} height={260} />
                )}
              </Card>
            </Col>

            <Col span={10}>
              <Card title="By Type" bordered={false}>
                <div style={{ paddingTop: 8 }}>
                  {SUBSCRIPTION_TYPES.map(({ key, label, color }) => {
                    const count = stats?.typeBreakdown?.[key] ?? 0;
                    const pct = total > 0 ? ((count / total) * 100).toFixed(1) : '0.0';
                    return (
                      <div key={key} style={{ marginBottom: 14 }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4, fontSize: 13 }}>
                          <span style={{ fontWeight: 500 }}>{label}</span>
                          <span style={{ color: '#666' }}>
                            {count.toLocaleString()} <span style={{ color: '#aaa' }}>({pct}%)</span>
                          </span>
                        </div>
                        <div style={{ background: '#f0f0f0', borderRadius: 4, height: 8, overflow: 'hidden' }}>
                          <div
                            style={{
                              width: `${pct}%`,
                              height: '100%',
                              background: color,
                              borderRadius: 4,
                              transition: 'width 0.3s',
                            }}
                          />
                        </div>
                      </div>
                    );
                  })}
                </div>
              </Card>
            </Col>
          </Row>
        </Spin>
      </Card>
    </div>
  );
};

export default SubscriberStats;
